/**
 * パントリー商品選択モジュールのビジネスロジック（純粋関数）
 *
 * v1 `<pantry-products>` (`assets/product-form.js` 939-1300 行) の選択集計と
 * バリデーションを純粋関数として再実装したもの。kd-pantry-products から呼び出す。
 *
 * @see docs/business-rules/pantry.md
 */

/**
 * 選択中のパントリー子商品 1 件分のエントリ。
 *
 * カート投入時の properties 構築（issue #3090）に必要な情報を含む。
 */
export interface SelectedPantryEntry {
  /** バリアント ID */
  variantId: string;
  /** 選択数量（> 0） */
  quantity: number;
  /** 単価（円） */
  variantPrice: number;
  /** 重量（g） */
  weightG: number;
  /** 在庫数 */
  inventoryQuantity: number;
  /** 最大購入数（未指定時は Infinity 相当） */
  maxQuantity: number;
  /** 商品タイトル */
  title: string;
  /** SKU */
  sku: string;
  /** 賞味期限（YYYY-MM-DD） */
  expirationOn: string;
  /** 内容量（product.metafields.product.volume）。空なら property を付与しない */
  volume?: string;
}

/** 商品選択グリッドの並び替えキー（ツールバーの <select value>）。 */
export type PantrySortKey = 'popular' | 'expiration' | 'price';

/** クライアント絞り込み / 並び替えに使うカード 1 枚分のメタ情報。 */
export interface PantryCardMeta {
  /** DOM 上の元の並び順（0 始まり）。人気順 = この昇順。安定ソートの tie-break にも使う。 */
  index: number;
  /** カテゴリ（product.type）。絞り込み用。 */
  category: string;
  /** 価格（数値。安い順の昇順キー）。 */
  price: number;
  /** 賞味期限（YYYY-MM-DD）。近い順の昇順キー。空文字は最後尾扱い。 */
  expiration: string;
}

/**
 * カードが指定カテゴリで可視かを返す。
 * activeCategory が空文字（＝「全て」）ならすべて可視。
 */
export function isPantryCardVisible(category: string, activeCategory: string): boolean {
  return activeCategory === '' || category === activeCategory;
}

/**
 * ISO 日付（YYYY-MM-DD）でない賞味期限（空文字・"-（賞味期限）" 等のプレースホルダ）を
 * 「不明」として近い順の最後尾へ送るためのセンチネル。
 */
const EXPIRATION_SENTINEL = '9999-99-99';

function normalizeExpiration(value: string): string {
  return /^\d{4}-\d{2}-\d{2}/.test(value) ? value : EXPIRATION_SENTINEL;
}

/**
 * 指定ソートキーでカードを並べ替え、元 index → 表示順位（0 始まり）の Map を返す。
 * 呼び出し側は各カードに `style.order = String(順位)` を設定してグリッド上で並べ替える
 * （DOM を移動しないため、カード内の選択状態・数量 peer 同期を壊さない）。
 *
 * - popular    : 元の並び順（index 昇順）
 * - price      : 価格 昇順（安い順）。同値は index 昇順
 * - expiration : 賞味期限 昇順（近い順）。ISO 日付でない値（空・"-（賞味期限）" 等）は最後尾。同値は index 昇順
 *
 * すべて安定ソート（同値は元 index 昇順）。
 */
export function computePantryCardOrder(
  cards: PantryCardMeta[],
  sortKey: PantrySortKey,
): Map<number, number> {
  const sorted = [...cards].sort((a, b) => {
    if (sortKey === 'price') {
      if (a.price !== b.price) return a.price - b.price;
    } else if (sortKey === 'expiration') {
      const ax = normalizeExpiration(a.expiration);
      const bx = normalizeExpiration(b.expiration);
      if (ax !== bx) return ax < bx ? -1 : 1;
    }
    // popular もしくは同値: 元の並び順で安定させる
    return a.index - b.index;
  });
  const order = new Map<number, number>();
  sorted.forEach((card, position) => order.set(card.index, position));
  return order;
}

/**
 * エントリ群を 1 パスで集計した結果。
 */
export interface PantryTotals {
  /** 商品代金合計（円、送料抜き）。最低注文金額チェック / 残額表示の基準。 */
  total: number;
  /** 一般会員 送料込み合計（円）= total + 親 base 送料 */
  totalGeneral: number;
  /** プレミアム会員 送料込み合計（円）= total + 親 base 送料（プレミアムは 0） */
  totalPremium: number;
  /** 合計個数 */
  count: number;
  /** 合計重量（g） */
  weightG: number;
  /** カード単位で在庫超過しているエントリがあるか */
  hasOverStock: boolean;
  /** カード単位で最大購入数を超過しているエントリがあるか */
  hasMaxExceeded: boolean;
  /** 合計重量が 1 注文あたりの最大重量を超過しているか（配送・梱包制約。issue #3179） */
  hasOverWeight: boolean;
}

/**
 * エントリ群を 1 パスで集計する。
 *
 * 会員別 送料込み合計は親パントリー商品の base 送料を 1 回だけ加算する
 * （子は送料計算対象外、cool は親も 0。SSoT: docs/business-rules/shipping.md §6.1）。
 * プレミアムは base 送料無料のため basePremium=0 で general との差額がそのまま割引額になる。
 * 空ボックス（count=0）は送料が発生しないため両会員とも 0 とする。
 *
 * @param baseGeneral - 親商品の一般会員 base 送料（円、metafield 由来。既定 0）
 * @param basePremium - 親商品のプレミアム会員 base 送料（円、metafield 由来 = 0。既定 0）
 * @param maxBoxWeightG - 1 注文あたりの最大重量（g、metafield 由来）。0 以下は上限なし扱い。
 */
export function calcPantryTotals(
  entries: SelectedPantryEntry[],
  baseGeneral = 0,
  basePremium = 0,
  maxBoxWeightG = 0,
): PantryTotals {
  let total = 0;
  let count = 0;
  let weightG = 0;
  let hasOverStock = false;
  let hasMaxExceeded = false;
  for (const e of entries) {
    total += e.variantPrice * e.quantity;
    count += e.quantity;
    weightG += e.weightG * e.quantity;
    if (e.quantity > e.inventoryQuantity) hasOverStock = true;
    if (Number.isFinite(e.maxQuantity) && e.quantity > e.maxQuantity) {
      hasMaxExceeded = true;
    }
  }
  const totalGeneral = count > 0 ? total + baseGeneral : 0;
  const totalPremium = count > 0 ? total + basePremium : 0;
  const hasOverWeight = maxBoxWeightG > 0 && weightG > maxBoxWeightG;
  return {
    total,
    totalGeneral,
    totalPremium,
    count,
    weightG,
    hasOverStock,
    hasMaxExceeded,
    hasOverWeight,
  };
}
